import { useEffect, useMemo, useRef } from "react";
import { useSession } from "./SessionProvider";
import { PokemonSprite } from "./PokemonSprite";
import { findPokemon } from "@/lib/pokemon";
import {
  X,
  Calendar,
  MapPin,
  Star,
  Sparkles,
  ArrowLeftRight,
  Info,
  Check,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { VariantGrid } from "./VariantTrackers";
import {
  buildDetailViewModel,
  formatFirstCaught,
  formatIvPercent,
  formatIvTriple,
  type DetailViewModel,
  type PokemonRecordLike,
} from "@/lib/pokemonRecordSelect";
import { dexNumberLabel } from "@/lib/dexNumberLabel";

const UNKNOWN = "-";

export function PokemonDetailSheet() {
  const { pokemonDetailId, openPokemonDetail, closePokemonDetail } = useSession();
  const [, setLocation] = useLocation();

  // Pull the trainer's records once (cached by React Query) and project
  // them into a provenance-aware view-model. The modal renders
  // "scan-derived facts only" — anything not in the chosen record stays
  // blank/unknown rather than falling back to mock values.
  const recordsQ = useQuery<{ records?: ReadonlyArray<PokemonRecordLike> }>({
    queryKey: ["/api/dex/records"],
    staleTime: 60_000,
    enabled: pokemonDetailId != null,
    refetchOnMount: "always",
  });
  const records = recordsQ.data?.records;
  const vm = pokemonDetailId != null
    ? buildDetailViewModel(records, pokemonDetailId)
    : null;

  // Push 4 follow-up (v1.4.1 b9): horizontal swipe + prev/next nav between
  // sibling detail pages in Nexus Dex. Siblings = unique species ids the
  // trainer has a record for, sorted by speciesId. Falls back to the
  // viewed species alone (no siblings) when records aren't loaded yet.
  const siblingIds = useMemo<number[]>(() => {
    if (!records) return pokemonDetailId != null ? [pokemonDetailId] : [];
    const seen = new Set<number>();
    for (const r of records) {
      const sid = (r as { speciesId?: number; pokemonId?: number }).speciesId
        ?? (r as { pokemonId?: number }).pokemonId;
      if (typeof sid === "number" && sid > 0) seen.add(sid);
    }
    if (pokemonDetailId != null) seen.add(pokemonDetailId);
    return Array.from(seen).sort((a, b) => a - b);
  }, [records, pokemonDetailId]);

  const currentIdx = pokemonDetailId != null ? siblingIds.indexOf(pokemonDetailId) : -1;
  const prevId = currentIdx > 0 ? siblingIds[currentIdx - 1] : null;
  const nextId = currentIdx >= 0 && currentIdx < siblingIds.length - 1
    ? siblingIds[currentIdx + 1]
    : null;

  // Touch tracking for horizontal swipe nav. Drag threshold mirrors the
  // wouter/Box swipe behaviour: 60 px horizontal AND less than 30 px
  // vertical to count as a horizontal swipe.
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  function onTouchStart(e: React.TouchEvent<HTMLDivElement>) {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(e: React.TouchEvent<HTMLDivElement>) {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = Math.abs(t.clientY - start.y);
    if (Math.abs(dx) < 60 || dy > 30) return;
    if (dx < 0 && nextId != null) {
      openPokemonDetail(nextId);
    } else if (dx > 0 && prevId != null) {
      openPokemonDetail(prevId);
    }
  }

  // Keyboard arrow nav for desktop. Esc closes (matches the X button).
  useEffect(() => {
    if (pokemonDetailId == null) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        closePokemonDetail();
      } else if (ev.key === "ArrowLeft" && prevId != null) {
        openPokemonDetail(prevId);
      } else if (ev.key === "ArrowRight" && nextId != null) {
        openPokemonDetail(nextId);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pokemonDetailId, prevId, nextId, openPokemonDetail, closePokemonDetail]);

  if (pokemonDetailId == null) return null;
  const p = findPokemon(pokemonDetailId);
  if (!p || !vm) return null;

  function generateTrade() {
    closePokemonDetail();
    setLocation(`/trade?single=${p?.id}`);
  }

  const ivText = formatIvTriple(vm.iv);
  const ivPct = formatIvPercent(vm.iv);
  const firstCaughtText = formatFirstCaught(vm.firstCaught.iso);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm anim-fade-up"
      onClick={closePokemonDetail}
      role="dialog"
      aria-label={`${p.name} detail`}
    >
      <div
        className="w-full md:max-w-md v2-neon-card rounded-t-3xl md:rounded-3xl max-h-[92dvh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ background: "var(--surface-dark-7)" }}
      >
        <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none rounded-t-3xl md:rounded-3xl">
          <div className="v2-aurora v2-aurora--green" style={{ width: "30vmax", height: "30vmax", top: "-12vmax", right: "-10vmax", opacity: 0.32 }} />
        </div>

        <div className="relative px-6 pt-6 pb-5">
          {/* Push 4 follow-up (v1.4.1 b9): back button on the top-left
              so users have an explicit "Back to Dex" affordance — the X
              close button on the top-right stays for consistency with
              modal dismiss patterns elsewhere in the app. */}
          <button
            onClick={closePokemonDetail}
            data-testid="button-back-pkmn-detail"
            className="absolute top-3 left-3 inline-flex items-center gap-1 h-8 px-2.5 rounded-full z-10 text-[12px] font-semibold"
            style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-ink-bright-faint)" }}
            aria-label="Back to Dex"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Dex</span>
          </button>
          <button
            onClick={closePokemonDetail}
            data-testid="button-close-pkmn-detail"
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center z-10"
            style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-ink-bright-faint)" }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          {/* Push 4 follow-up (v1.4.1 b9): prev / next sibling navigation.
              Renders fallback chevrons even when only one sibling exists
              (disabled state) so the affordance is discoverable, and
              hides the arrows in the rare null-id state. */}
          {(prevId != null || nextId != null) && (
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 z-10"
              data-testid="pkmn-detail-pager"
            >
              <button
                disabled={prevId == null}
                onClick={() => prevId != null && openPokemonDetail(prevId)}
                data-testid="button-prev-pkmn-detail"
                className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{
                  background: "var(--surface-dark-9)",
                  border: "1px solid var(--accent-fill-glow)",
                  color: "var(--text-ink-bright-faint)",
                  opacity: prevId == null ? 0.4 : 1,
                }}
                aria-label="Previous Pokémon"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span
                className="font-mono text-[10px] tabular-nums px-1.5"
                style={{ color: "var(--text-ink-faint)" }}
                data-testid="pkmn-detail-pager-counter"
              >
                {currentIdx >= 0 ? `${currentIdx + 1}/${siblingIds.length}` : ""}
              </span>
              <button
                disabled={nextId == null}
                onClick={() => nextId != null && openPokemonDetail(nextId)}
                data-testid="button-next-pkmn-detail"
                className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{
                  background: "var(--surface-dark-9)",
                  border: "1px solid var(--accent-fill-glow)",
                  color: "var(--text-ink-bright-faint)",
                  opacity: nextId == null ? 0.4 : 1,
                }}
                aria-label="Next Pokémon"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-start gap-4">
            {/*
              v1.3.2(9): large circular scan-verified portrait — neon-green
              ring on a softened grey/green inner well. Sam's b8 feedback:
              pure-black wells were jolting when captured sprite backgrounds
              were white/blue/pale; the slight green tint on the well now
              cushions that transition without losing the Nexus Dex look.
              The sprite inside is rendered through PokemonSprite which
              resolves the user's locally-captured crop via localSpriteStore
              when one exists; bundled official sprites are not introduced
              here.
            */}
            <div
              className="relative shrink-0 rounded-full"
              style={{
                width: 132,
                height: 132,
                background: "linear-gradient(180deg, var(--accent-fill), var(--accent-fill))",
                boxShadow: "0 0 16px var(--accent-fill-glow)",
              }}
              data-testid="pkmn-detail-portrait"
            >
              <div
                aria-hidden
                className="absolute rounded-full"
                style={{
                  inset: 3,
                  // Softened grey/green inner well (replaces pure-black
                  // var(--surface-dark-7) used through b7). Reads as a calmer
                  // Nexus Dex card surface; pale captured backgrounds no
                  // longer jolt against sheer black.
                  background: "radial-gradient(circle at 50% 35%, hsl(150 22% 18%) 0%, hsl(142 28% 11%) 70%, hsl(142 30% 9%) 100%)",
                }}
              />
              <div
                className="absolute rounded-full overflow-hidden flex items-center justify-center"
                data-testid="pkmn-detail-portrait-crop"
                style={{ inset: 4 }}
              >
                <PokemonSprite id={p.id} status={vm.caught ? "caught" : "missing"} category="main" size={120} />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px]" style={{ color: "var(--text-ink-faint)" }}>
                {dexNumberLabel(p.id)}
              </p>
              <h2 className="v2-display text-2xl tracking-tight leading-tight" style={{ color: "var(--text-ink-bright)" }} data-testid="pkmn-detail-name">
                {p.name}
              </h2>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <Chip>Gen {p.gen}</Chip>
                {p.legendary && <Chip tone="lucky">Legendary</Chip>}
                {p.mythical && <Chip tone="perfect">Mythical</Chip>}
                {vm.lucky === true && <Chip tone="lucky">Lucky</Chip>}
                <ProvenanceChip vm={vm} />
              </div>
            </div>
          </div>
        </div>

        <div className="relative px-6 pb-7 space-y-5">
          {/* Stats — only render verified scan-derived values; otherwise show — */}
          <section>
            <p className="v2-display-up text-[10px] mb-2" style={{ color: "var(--accent-fill)" }}>Stats</p>
            <div className="grid grid-cols-3 gap-2">
              <StatMini label="HP" stat={vm.hp} />
              <StatMini label="CP" stat={vm.cp} />
              <StatMini
                label="IV"
                // IV is composite: show the triple as the value when verified,
                // otherwise fall back to a "pending" tile.
                stat={{ value: vm.iv.verified ? Math.round(((vm.iv.atk! + vm.iv.def! + vm.iv.sta!) / 45) * 100) : null, verified: vm.iv.verified }}
                suffix={vm.iv.verified ? "%" : undefined}
                hint={ivText ?? undefined}
              />
            </div>
          </section>

          {/* Status row */}
          <div className="grid grid-cols-3 gap-2">
            <StatusTile label="Caught" value={vm.caught ? "Yes" : "No"} dim={!vm.caught} />
            <StatusTile label="Lucky" value={vm.lucky === true ? "Yes" : vm.lucky === false ? "No" : UNKNOWN} dim={vm.lucky !== true} />
            <StatusTile label="Shiny" value={vm.shiny === true ? "Yes" : vm.shiny === false ? "No" : UNKNOWN} dim={vm.shiny !== true} />
          </div>

          <VariantGrid speciesId={p.id} />

          {/* Encounter Stats — every row is scan-derived or marked pending. */}
          <section>
            <p className="v2-display-up text-[10px] mb-2" style={{ color: "var(--accent-fill)" }}>Encounter</p>
            <div className="v2-neon-card-soft overflow-hidden" data-testid="encounter-stats">
              <Stat
                icon={<Calendar className="w-3.5 h-3.5" />}
                label="First caught"
                value={firstCaughtText ?? "Pending scan"}
                verified={vm.firstCaught.verified}
              />
              <Stat
                icon={<MapPin className="w-3.5 h-3.5" />}
                label="Catch source"
                value={vm.catchSource.value ?? "Unknown"}
                verified={vm.catchSource.verified}
              />
              <Stat
                icon={<Star className="w-3.5 h-3.5" />}
                label="Best IV"
                value={ivText ? `${ivText}${ivPct ? ` (${ivPct}%)` : ""}` : "Unknown"}
                verified={vm.iv.verified}
              />
              {vm.lucky === true && (
                <Stat
                  icon={<Sparkles className="w-3.5 h-3.5" />}
                  label="Lucky"
                  value="Yes"
                  verified={vm.provenance === "scan"}
                />
              )}
            </div>
            <div
              className="mt-2 flex items-start gap-2 rounded-xl px-3 py-2.5"
              style={{ background: "var(--surface-dark-9)", border: "1px dashed var(--accent-fill-glow)" }}
              data-testid="encounter-disclosure"
            >
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "var(--text-ink-faint)" }} />
              <p className="text-[11px] leading-relaxed" style={{ color: "var(--text-ink-soft)" }}>
                {disclosureFor(vm)}
              </p>
            </div>
          </section>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              onClick={closePokemonDetail}
              data-testid="button-pkmn-detail-close"
              className="rounded-full py-2.5 text-sm font-semibold"
              style={{
                background: "var(--surface-dark-9)",
                border: "1px solid var(--accent-fill-glow)",
                color: "var(--text-ink-bright-faint)",
              }}
            >
              Close
            </button>
            <button
              onClick={generateTrade}
              data-testid="button-pkmn-detail-trade"
              className="v2-neon-cta py-2.5 rounded-full inline-flex items-center justify-center gap-1.5"
              disabled={!vm.caught}
              style={!vm.caught ? { opacity: 0.5 } : undefined}
            >
              <ArrowLeftRight className="w-3.5 h-3.5" /> Trade ready
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function disclosureFor(vm: DetailViewModel): string {
  if (!vm.hasRecord) {
    return "No scan yet for this Pokémon. Stats and encounter details will appear after a Live Scan or screenshot import.";
  }
  if (vm.provenance === "scan") {
    return "Encounter facts above come from your most recent scan of this Pokémon.";
  }
  return "Only the basic caught flag is verified for this Pokémon. Scan it via Live Scan or upload a detail screenshot to populate stats and encounter details.";
}

function ProvenanceChip({ vm }: { vm: DetailViewModel }) {
  if (!vm.caught) {
    return (
      <span
        data-testid="chip-not-caught"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5"
        style={{
          background: "var(--surface-dark-12)",
          color: "var(--text-ink-faint)",
          border: "1px solid hsl(142 30% 22%)",
        }}
      >
        <HelpCircle className="w-3 h-3" strokeWidth={2.5} />
        Not caught
      </span>
    );
  }
  if (vm.provenance === "scan") {
    return (
      <span
        data-testid="chip-confirmed"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5"
        style={{
          background: "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
          color: "var(--text-on-accent)",
          boxShadow: "0 0 10px var(--accent-fill-glow)",
        }}
      >
        <Check className="w-3 h-3" strokeWidth={3} />
        Scan verified
      </span>
    );
  }
  return (
    <span
      data-testid="chip-pending"
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5"
      style={{
        background: "var(--surface-dark-12)",
        color: "hsl(43 96% 70%)",
        border: "1px solid hsl(43 96% 60% / 0.45)",
      }}
    >
      <HelpCircle className="w-3 h-3" strokeWidth={2.5} />
      Pending scan
    </span>
  );
}

function Chip({ children, tone }: { children: React.ReactNode; tone?: "lucky" | "perfect" }) {
  const c = tone === "lucky"
    ? "hsl(43 96% 60%)"
    : tone === "perfect"
      ? "hsl(326 92% 70%)"
      : "var(--text-ink-bright-faint)";
  return (
    <span
      className="text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5"
      style={{
        background: tone ? `${c.replace(")", " / 0.16)")}` : "var(--surface-dark-9)",
        color: c,
        border: `1px solid ${tone ? c.replace(")", " / 0.45)") : "var(--accent-fill-glow)"}`,
      }}
    >
      {children}
    </span>
  );
}

function StatMini({
  label,
  stat,
  suffix,
  hint,
}: {
  label: string;
  stat: { value: number | null; verified: boolean };
  suffix?: string;
  hint?: string;
}) {
  const display = stat.value !== null ? `${stat.value}${suffix ?? ""}` : UNKNOWN;
  const dim = !stat.verified;
  return (
    <div
      className="v2-neon-card-soft p-3 text-center"
      data-testid={`stat-${label.toLowerCase()}`}
      data-verified={stat.verified ? "true" : "false"}
      title={hint}
      style={dim ? { opacity: 0.7 } : undefined}
    >
      <p className="v2-display-up text-[9px]" style={{ color: dim ? "hsl(142 18% 60%)" : "var(--text-ink-bright-faint)" }}>{label}</p>
      <p
        className="v2-display tabular-nums text-xl mt-0.5"
        style={{ color: dim ? "var(--text-ink-muted)" : "var(--text-ink-bright)" }}
      >
        {display}
      </p>
      <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: "hsl(230 25% 12%)" }}>
        <div
          className="h-full rounded-full"
          style={{
            width: stat.value !== null ? `${Math.min(100, stat.value / 2.5)}%` : "0%",
            background: dim
              ? "linear-gradient(90deg, hsl(142 20% 30%), hsl(142 20% 22%))"
              : "linear-gradient(90deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
            boxShadow: dim ? undefined : "0 0 6px var(--accent-fill-glow)",
          }}
        />
      </div>
    </div>
  );
}

function StatusTile({ label, value, dim }: { label: string; value: string; dim?: boolean }) {
  return (
    <div
      className="rounded-xl px-3 py-2.5 text-center"
      style={{
        background: dim ? "hsl(230 25% 8%)" : "var(--accent-fill-glow-faint)",
        border: `1px solid ${dim ? "hsl(142 30% 22%)" : "var(--accent-fill-glow)"}`,
      }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: dim ? "hsl(142 18% 60%)" : "var(--accent-fill)" }}>{label}</p>
      <p className="font-mono text-sm font-bold mt-0.5" style={{ color: dim ? "var(--text-ink-faint)" : "var(--text-ink-bright)" }}>{value}</p>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  verified,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  verified: boolean;
}) {
  return (
    <div
      className="flex items-center gap-2.5 px-4 py-3 border-b last:border-b-0"
      style={{ borderColor: "var(--accent-fill-glow-faint)" }}
      data-verified={verified ? "true" : "false"}
    >
      <span style={{ color: verified ? "var(--accent-fill)" : "var(--text-ink-faint)" }}>{icon}</span>
      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "hsl(142 18% 72%)" }}>{label}</span>
      <span
        className="ml-auto text-[12px] font-medium text-right"
        style={{ color: verified ? "var(--text-ink-bright)" : "var(--text-ink-faint)" }}
      >
        {value}
      </span>
    </div>
  );
}
